import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Applications } from 'src/app/shared/data/application';
import { Products } from 'src/app/shared/data/feedback-product';
import {
  CanEditByDefault,
  CanViewByDefault,
  FeatureHasEdit,
  FeatureHasCustomPermission,
  Features,
} from 'src/app/shared/data/application-feature';
import { Companies } from 'src/app/shared/data/companies';
import { UserNotification, UserView } from 'src/app/shared/data/data.generated';
import { UserNotificationTypes } from 'src/app/shared/data/user-notification-type';
import { ApiService } from 'src/app/shared/services/api.service';
import { SelectOption, SnackbarV3Service } from '@gea/digital-ui-lib';
import { MenuItem } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Role, Roles } from 'src/app/shared/data/roles';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'user-editor',
  templateUrl: './user-editor.component.html',
  styleUrls: ['./user-editor.component.scss'],
  standalone: false,
})
export class UserEditorComponent implements OnInit {
  constructor(
    private readonly api: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: SnackbarV3Service,
    private fb: FormBuilder,
    private utilityService: UtilityService,
  ) {}

  public isNew: boolean = true;
  public id: number = 0;
  public user: UserView = new UserView();
  public orderStatusData: { name: string; value: string }[] = [];
  public permissions: any[] = [];
  public notifications: UserNotification[] = [];
  public userNotificationTypes = UserNotificationTypes;
  public companies = Companies;
  public applications = this.getAllAppsAndProducts(Applications, Products);
  public FeatureHasEdit = FeatureHasEdit;
  public FeatureHasCustomPermission = FeatureHasCustomPermission;

  tabMenuItems: MenuItem[] = [
    {
      label: 'Personal Data',
      tabindex: '0',
    },
    {
      label: 'Permissions',
      tabindex: '1',
    },
    {
      label: 'Notifications',
      tabindex: '2',
    },
  ];
  permissionsList: SelectOption[] = [];
  activeTabItem = this.tabMenuItems[0];
  isUserLoading: boolean = true;
  isSaveDisabled: boolean = true;
  userPersonalDataForm = this.fb.group({
    userName: ['', [Validators.required]],
    email: ['', [Validators.required]],
    role: [''],
    orderStatusFilter: [''],
    orderStatusNotificationName: [{ name: '', value: '' }],
    siteFilter: [''],
    lastLogin: [''],
  });
  userPermissionsForm: FormGroup;
  areFiltersVisible: boolean = false;
  isOrderStatusFilterVisible: boolean = false;
  isSiteFilterVisible: boolean = false;
  isExternalSalesRoleSelected: boolean = false;
  isCustomerRoleSelected: boolean = false;

  ngOnInit(): void {
    this.permissionsList = Roles.map((role) => ({
      value: role.Id,
      name: role.Name,
    }));
    this.api.Filters.GetMultipleAutoLists(
      '',
      'Planner,ProjectManager,SalesManager',
      'OrderStatus',
    ).subscribe({
      next: (data) => {
        this.orderStatusData = Object.values(data)
          .flat()
          .map((item) => ({ name: item as string, value: item as string }));
        this.orderStatusData.push({ name: 'Manager', value: 'Manager' });
      },
      error: (err) => {
        this.snackbar.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error fetching order status data: ' + err,
        });
      },
    });
    this.route.params.subscribe((p: any) => {
      this.isNew = p['isNew'].toLowerCase() == 'true';
      this.id = Number.parseFloat(p['id']);
      if (!this.isNew) {
        this.api.Users.GetUserDetails(this.id).subscribe({
          next: (user) => {
            this.user = user;
            this.api.Users.GetUserApplicationFeatures(this.id, '').subscribe({
              next: (appFeatures) => {
                for (let appFeature of appFeatures.Features) {
                  this.permissions
                    .filter((x) => x.Id == appFeature.FeatureId)
                    .forEach((x) => {
                      x.CanView = appFeature.CanView;
                      x.CanEdit = appFeature.CanEdit;
                      x.CustomPermission = appFeature.CustomPermission;
                    });
                }
              },
              error: (err) => {
                this.snackbar.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error fetching user application features: ' + err,
                });
              },
            });
            this.api.Users.GetUserNotifications(this.id).subscribe({
              next: (res) => {
                this.notifications = res;
              },
              error: (err) => {
                this.snackbar.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error fetching user notifications: ' + err,
                });
              },
            });
          },
          error: (err) => {
            this.snackbar.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error fetching user details: ' + err,
            });
          },
          complete: () => {
            this.userPersonalDataForm.setValue({
              userName: this.user.Name,
              email: this.user.Email,
              role: this.user.RoleId.toString(),
              orderStatusFilter: this.user.OrderStatusFilters,
              orderStatusNotificationName: {
                name: this.user.OrderStatusNotificationName || '',
                value: this.user.OrderStatusNotificationName || '',
              },
              siteFilter: this.user.SiteFilter,
              lastLogin: this.utilityService.formatDateWithTime(
                this.user.LastModified,
              ),
            });
            this.userPermissionsForm = this.fb.group({
              role: [this.getRole(this.user.RoleId), Validators.required],
            });
            this.isUserLoading = false;
          },
        });
      } else {
        this.isUserLoading = false;
        this.user = new UserView();
        this.user.RoleId = 0;
        this.userPermissionsForm = this.fb.group({
          role: [this.getRole(this.user.RoleId)],
        });
      }
    });
    this.permissions = Features.map((x) => {
      return { Id: x.Id, Name: x.Name, CanView: false, CanEdit: false };
    });
    this.setDefaultPermissions();
  }

  setDefaultPermissions() {
    for (let permission of this.permissions) {
      permission.CanView = CanViewByDefault(permission.Id, this.user.RoleId);
      permission.CanEdit = CanEditByDefault(permission.Id, this.user.RoleId);
    }
  }

  onRoleChange(event: any) {
    const selectedRole = this.permissionsList.find(
      (option) => option.value === Number(event.value),
    );
    for (let permission of this.permissions) {
      const roleValue: Role = selectedRole?.value ?? Role.Guest;
      permission.CanView = CanViewByDefault(permission.Id, roleValue);
      permission.CanEdit = CanEditByDefault(permission.Id, roleValue);
    }
    this.isSiteFilterVisible = (selectedRole?.value ?? 0) > Role.Guest;
    this.isOrderStatusFilterVisible =
      (selectedRole?.value ?? 0) >= Role.ExternalSales;
    // Handle Order Status Filter Validator
    this.isExternalSalesRoleSelected =
      (selectedRole?.value ?? 0) === Role.ExternalSales;
    const orderStatusControl =
      this.userPersonalDataForm.get('orderStatusFilter');
    if (this.isExternalSalesRoleSelected) {
      orderStatusControl?.setValidators(Validators.required);
    } else {
      orderStatusControl?.clearValidators();
    }
    orderStatusControl?.updateValueAndValidity();

    // Handle Site Filter Validator
    this.isCustomerRoleSelected =
      (selectedRole?.value ?? 0) === Role.Customer ||
      (selectedRole?.value ?? 0) === Role.PreferredCustomer;

    const siteFilterControl = this.userPersonalDataForm.get('siteFilter');
    if (this.isCustomerRoleSelected) {
      siteFilterControl?.setValidators(Validators.required);
    } else {
      siteFilterControl?.clearValidators();
    }
    siteFilterControl?.updateValueAndValidity();
  }

  getAllAppsAndProducts(applicationList: any, productList: any) {
    const allAppsAndProducts = [...applicationList, ...productList];
    return allAppsAndProducts;
  }

  viewChanged(permission: any) {
    if (!permission.CanView) permission.CanEdit = false;
  }

  editChanged(permission: any) {
    if (permission.CanEdit) permission.CanView = true;
  }

  customPermissionChanged(permission: any) {
    if (permission.CustomPermission) permission.CanView = true;
  }

  addNotification() {
    let notification = new UserNotification();
    notification.UserId = this.id;
    this.notifications.push(notification);
  }

  deleteNotification(notification: UserNotification) {
    this.notifications = this.notifications.filter(
      (item) => item !== notification,
    );
  }

  save() {
    if (this.userPersonalDataForm.invalid || this.userPermissionsForm.invalid) {
      this.snackbar.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields correctly.',
      });
      return;
    }

    this.isUserLoading = true;
    this.user.Name = this.userPersonalDataForm.value.userName ?? '';
    this.user.Email = this.userPersonalDataForm.value.email ?? '';
    const roleField = this.userPermissionsForm.value.role;
    if (roleField && typeof roleField === 'object' && 'value' in roleField) {
      this.user.RoleId = Number(roleField.value);
    } else {
      const selectedRoleOption = this.permissionsList.find(
        (role) => role.name === roleField,
      );
      this.user.RoleId = selectedRoleOption
        ? Number(selectedRoleOption.value)
        : 0;
    }
    this.user.OrderStatusFilters =
      this.userPersonalDataForm.value.orderStatusFilter ?? '';
    const notificationName =
      this.userPersonalDataForm.value.orderStatusNotificationName;
    this.user.OrderStatusNotificationName =
      typeof notificationName === 'string'
        ? notificationName
        : (notificationName?.value ?? '');

    this.user.SiteFilter = this.userPersonalDataForm.value.siteFilter ?? '';
    if (this.isNew) {
      this.snackbar.add({
        severity: 'info',
        summary: 'Creating User',
        detail: 'Creating user, please wait...',
      });
      this.api.Users.CreateUser(this.user, this.permissions).subscribe({
        next: (res: any) => {
          if (res.message === 'User with this email already exists') {
            this.snackbar.add({
              severity: 'error',
              summary: 'Error',
              detail: res.message,
            });
            this.router.navigate(['/users']);
          } else {
            this.snackbar.add({
              severity: 'success',
              summary: 'User Created',
              detail: 'User has been created successfully.',
            });
            this.router.navigate(['/users']);
          }
        },
        error: (error) => {
          this.snackbar.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error creating user: ' + error,
          });
        },
      });
    } else {
      this.snackbar.add({
        severity: 'info',
        summary: 'Updating User',
        detail: 'Updating user, please wait...',
      });
      this.api.Users.UpdateUser(
        this.user,
        this.permissions,
        this.notifications,
      ).subscribe({
        next: (res: any) => {
          if (this.user.Email.toLowerCase() == this.api.Email.toLowerCase()) {
            this.api.Users.GetUserApplicationFeatures(
              0,
              this.user.Email,
            ).subscribe((x) => {
              sessionStorage.setItem('AppFeatures', JSON.stringify(x));
              this.api.AppFeatures = x.Features;
              this.router.navigate(['/users']);
            });
          } else {
            this.router.navigate(['/users']);
          }
          this.snackbar.add({
            severity: 'success',
            summary: 'User Updated',
            detail: 'User has been updated successfully.',
          });
        },
        error: (error) => {
          this.snackbar.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error updating user: ' + error.message,
          });
        },
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/users']);
  }

  goToTab(activeTabItem: MenuItem): void {
    this.activeTabItem = activeTabItem;
  }

  getRole(roleId: number): string {
    const role = Roles.find((r) => r.Id === roleId);
    return role ? role.Name : '';
  }

  // formatDateWithTime(dateStr: string): string {
  //   if (!dateStr) return '';
  //   const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  //   const date = new Date(utcDateString);
  //   return date.toLocaleString('en-US', {
  //     hour: 'numeric',
  //     minute: '2-digit',
  //     day: '2-digit',
  //     month: '2-digit',
  //     year: 'numeric',
  //   });
  // }

  onOrderStatusNameChange(event: any, value: string) {
    this.userPersonalDataForm.patchValue({
      [value]: event.value,
    });
  }

  cancel() {
    this.router.navigate(['/users', {}]);
  }
}
